import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { DateFormat, DateTimeHelper } from '@absg/angular-platform';
import { AuthorizationApi } from '@absg/api-entitlements';
import { MedicalEquivalentApi, ProductDetail } from '@absg/api-formulary';
import { IdentityApi, RopcUserContract, UserClaims } from '@absg/api-identity';
import {
  EventDetailInfo, InventoryItemProduct, InventoryItemSummary, Transaction,
  TransactionRequest, WitnessAdjustApi,
} from '@absg/api-inventory';
import { PracticeClinicDetail, UserContextValidation, UsersApi, ValidateContextResponse } from '@absg/api-party';
import {
  BaseService, CalculationHelper, FormularyType, InventoryItem, InventoryTransactionTypes, ManagePosition, Position, PurchaseOrderDetailViewModel, StorageStation,
} from '@absg/nim-client-shared';
import { Injectable } from '@angular/core';
import { CommonDataService } from 'core.services/common-data.service';
import { StationInfoService } from 'core.services/station-info.service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { ParentComponentTypes } from 'shared.root/enums/parentcomponent.enum';
import { WitnessItem } from 'shared.root/models/witness-base.model';
import { InventoryManagementPermission } from 'shared.root/permissions/inventory-management.permission';

import { SettingsService } from './settings.service';

@Injectable({ providedIn: 'root' })
export class WitnessService extends BaseService {
  private practiceClinicInfo: BehaviorSubject<PracticeClinicDetail>;
  witnessTransactionIds = new BehaviorSubject<Array<number>>([]);

  witnessPermission = InventoryManagementPermission.witnessRequired;
  inActiveUser = false;
  errorMessage = 'Your account has been disabled. If you would like to enable your account, please contact your admin.';

  constructor(
    loggingService: LogService,
    private stationInfoService: StationInfoService,
    private commonDataService: CommonDataService,
    private settingsService: SettingsService,
    private witnessAdjustApi: WitnessAdjustApi,
    private identityApi: IdentityApi,
    private usersApi: UsersApi,
    private medicalEquivalentApi: MedicalEquivalentApi,
    private authorizationApi: AuthorizationApi,
  ) {
    super(loggingService);
    this.practiceClinicInfo = this.stationInfoService.practiceClinicDetail;
  }

  checkWitnessPermission(userId: number, practiceId: number): Observable<boolean> {
    return this.authorizationApi.getUserPermissions(userId, practiceId).pipe(take(1),
      map(response => {
        const foundPermission = AbsgArrayHelper.find((response), item => (Number(item.systemAsset) === this.witnessPermission.system &&
          Number(item.processAsset) === this.witnessPermission.process &&
          Number(item.actionType) === this.witnessPermission.action));
        return !AbsgArrayHelper.isNil(foundPermission);
      }));
  }

  mapInventoryItemsToWitnessItem(invItems: InventoryItem[], parentComponent: ParentComponentTypes): WitnessItem[] {
    let witnessItems: WitnessItem[] = [];
    let userInputLotWise: string[] = [];
    let uiLotQty = '';
    invItems.forEach(invItem => {
      // Normal Station Positions
      if (!AbsgArrayHelper.isNil(invItem.inventoryItemProducts) && invItem.inventoryItemProducts.length > 0) {
        if (!AbsgArrayHelper.isNil(invItem.requiredNote) && !AbsgArrayHelper.isNil(invItem.requiredNote.note) && invItem.requiredNote.note.length > 0) {
          userInputLotWise = invItem.requiredNote.note.split(',');
        }
        invItem.inventoryItemProducts.forEach(identifier => {
          if (!AbsgArrayHelper.isNil(identifier)) {
            uiLotQty = userInputLotWise.find(x => x.includes(identifier.lotNumber));
            const witnessItem = this.mapInventoryItemToWitnessItem(invItem, parentComponent);
            witnessItem.qtyOnHandInCabinet = identifier.lotQuantity;
            witnessItem.qtyOnHandInUTH = identifier.lotQuantityUnderhood;
            witnessItem.lotExpirationDate = identifier.expirationDate;
            witnessItem.lotNumber = identifier.lotNumber;
            witnessItem.userInputQtyInvUnitInCabinet = uiLotQty ? Number(uiLotQty.split(';')[1]) : 0;
            witnessItem.userInputQtyInvUnitInUTH = uiLotQty ? Number(uiLotQty.split(';')[2]) : 0;
            witnessItem.storagePosition = invItem.storagePosition;
            witnessItems.push(witnessItem);
          }
        });
      }

      if (witnessItems.filter(item => item.supplierProductInclusionId === invItem.supplierProductInclusionId).length === 0) {
        const witnessItem = this.mapInventoryItemToWitnessItem(invItem, parentComponent);
        witnessItem.qtyOnHandInCabinet = 0;
        witnessItem.qtyOnHandInUTH = 0;
        witnessItem.lotExpirationDate = null;
        witnessItem.lotNumber = '';
        witnessItem.userInputQtyInvUnitInCabinet = 0;
        witnessItem.userInputQtyInvUnitInUTH = 0;
        witnessItems.push(witnessItem);
      }
    });
    witnessItems = this.sortWitnessItemPositionAscending(witnessItems);
    return witnessItems;
  }

  mapInventoryItemsToWitnessItemSummary(invItems: InventoryItem[], parentComponent: ParentComponentTypes): WitnessItem[] {
    const witnessItems: WitnessItem[] = [];
    invItems.forEach(invItem => {

      if (witnessItems.filter(item => item.supplierProductInclusionId === invItem.supplierProductInclusionId).length === 0) {
        const witnessItem = this.mapInventoryItemToWitnessItem(invItem, parentComponent);
        witnessItem.doorName = this.getDoorName(invItem);
        witnessItem.qtyOnHandInCabinet = 0;
        witnessItem.qtyOnHandInUTH = 0;
        witnessItem.lotExpirationDate = null;
        witnessItem.lotNumber = '';
        witnessItems.push(witnessItem);
      }
    });
    return witnessItems;
  }

  mapManagePositionsToWitnessItem(managePositions: ManagePosition[], parentComponent: ParentComponentTypes): WitnessItem[] {
    let witnessItems: WitnessItem[] = [];
    managePositions.forEach(managePosition => {
      if (managePosition.isBinned()) {
        managePosition.productDetails.forEach(productDetail => {
          managePosition.productDetail = productDetail;
          witnessItems = this.AssignManagePositionsToWitness(managePosition, parentComponent, witnessItems);
          witnessItems.forEach(item => item.meiParentId = productDetail.meiParentId);
        });
        witnessItems = this.sortBinWitnessItems(witnessItems);
      } else {
        witnessItems = this.AssignManagePositionsToWitness(managePosition, parentComponent, witnessItems);
      }
      witnessItems = this.sortWitnessItemPositionAscending(witnessItems);
    });

    return witnessItems;
  }

  mapManagePositionsToWitnessItemSummary(managePositions: ManagePosition[], parentComponent: ParentComponentTypes): WitnessItem[] {
    const witnessItems: WitnessItem[] = [];
    managePositions.forEach(managePosition => {
      if (witnessItems.filter(item => item.supplierProductInclusionId ===
        managePosition.inventoryItem.supplierProductInclusionId).length === 0) {
        const witnessItem = this.mapManagePositionToWitnessItem(managePosition, parentComponent);
        witnessItem.qtyOnHandInCabinet = managePosition.inventoryItem.quantityOnHand;
        witnessItem.qtyOnHandInUTH = 0;
        witnessItem.userInputQtyInvUnitInUTH = 0;
        witnessItem.userInputQtyInvUnitInCabinet = 0;
        witnessItem.lotExpirationDate = null;
        witnessItem.lotNumber = managePosition.lotNumber;
        witnessItem.isAssigned = !AbsgArrayHelper.isNil(managePosition.packingSlipDetail) ? managePosition.packingSlipDetail.isAssigned : true;
        witnessItems.push(witnessItem);
      }
    });
    return witnessItems;
  }

  mapPurchaseOrderDetailViewModelToWitnessItems(orderLines: PurchaseOrderDetailViewModel[],
    parentComponent: ParentComponentTypes): WitnessItem[] {
    let witnessItems: WitnessItem[] = [];
    let inventoryItemProducts: InventoryItemProduct[] = [];
    AbsgArrayHelper.each(orderLines, data => {
      const productDetail = data;
      const inventoryItemSummary = this.stationInfoService.getInventoryItem(productDetail.supplierProductInclusionId);
      inventoryItemProducts = inventoryItemSummary.inventoryItemProducts;
      witnessItems = this.mapPurchaseOrderItems(productDetail,
        parentComponent, inventoryItemProducts, inventoryItemSummary, witnessItems);
    });
    witnessItems = this.sortWitnessItemPositionAscending(witnessItems);
    return witnessItems;
  }

  mapPurchaseOrderItems(orderLine: PurchaseOrderDetailViewModel,
    parentComponent: ParentComponentTypes, inventoryItemProducts: InventoryItemProduct[], inventoryItemSummary: InventoryItemSummary,
    witnessItems: WitnessItem[]): WitnessItem[] {
    if (!AbsgArrayHelper.isNil(inventoryItemProducts) && inventoryItemProducts.length > 0) {
      AbsgArrayHelper.each(inventoryItemProducts, identifier => {
        if (!AbsgArrayHelper.isNil(identifier) && (identifier.lotQuantity >= 0 || identifier.lotQuantityUnderhood >= 0)) {
          const witnessItem = this.mapPurchaseOrderDetailViewModelToWitnessItem(orderLine, parentComponent);
          witnessItem.qtyOnHandInCabinet = identifier.lotQuantity;
          witnessItem.qtyOnHandInUTH = identifier.lotQuantityUnderhood;
          witnessItem.lotExpirationDate = identifier.expirationDate;
          witnessItem.lotNumber = identifier.lotNumber;
          witnessItem.userInputQtyInvUnitInCabinet = 0;
          witnessItem.isMultiLotYN = inventoryItemProducts.length > 1;
          witnessItem.isDiscontinued = orderLine.discontinuedFlag;
          witnessItem.witnessUserPartyId = orderLine.witnessUserId;
          witnessItem.witnessUserFullName = orderLine.witnessUserName;
          witnessItem.isAssigned = true;
          witnessItem.inventoryItemId = identifier.inventoryItemId;
          witnessItems.push(witnessItem);
        }
      });
    } else if (!AbsgArrayHelper.isNil(inventoryItemProducts) && inventoryItemProducts.length === 0) {
      const witnessItem = this.mapPurchaseOrderDetailViewModelToWitnessItem(orderLine, parentComponent);
      witnessItem.qtyOnHandInCabinet = 0;
      witnessItem.qtyOnHandInUTH = 0;
      witnessItem.userInputQtyInvUnitInCabinet = 0;
      witnessItem.isMultiLotYN = false;
      witnessItem.isDiscontinued = orderLine.discontinuedFlag;
      witnessItem.witnessUserPartyId = orderLine.witnessUserId;
      witnessItem.witnessUserFullName = orderLine.witnessUserName;
      witnessItem.isAssigned = true;
      witnessItem.inventoryItemId = inventoryItemSummary.inventoryItemId;
      witnessItems.push(witnessItem);
    }

    return witnessItems;
  }

  // TODO: This should be simplified along with the witness item when remodel complete
  mapPositionsToWitnessItems(positions: Position[], parentComponent: ParentComponentTypes,
    witnessUserId = null, witnessUserName = null): WitnessItem[] {
    const witnessItems: WitnessItem[] = [];
    AbsgArrayHelper.each(positions, ps =>
      AbsgArrayHelper.each(ps.products, pr => {
        // Blank lot if the item is brand new
        if (pr.lots.length === 0) {
          pr.lots.push({
            expirationDate: null,
            lotNumber: null,
            lotQuantity: 0,
          } as InventoryItemProduct);
        }
        AbsgArrayHelper.each(pr.lots, lot => {
          const detail = pr.detail;
          const witnessItem = new WitnessItem();
          witnessItem.accessPointId = ps.getAssociatedAccessPointId();
          witnessItem.displayName = detail.displayName;
          witnessItem.alternateName = detail.tradeName;
          witnessItem.genericName = detail.genericName;
          witnessItem.gcnSequenceNumber = detail.gcnSequenceNumber;
          witnessItem.dispenseUnitOfMeasureConversionFactor = detail.dispenseUnitOfMeasureConversionFactor;
          witnessItem.inventoryUnitOfMeasureConversionFactor = detail.inventoryUnitOfMeasureConversionFactor;
          witnessItem.volumeUnitOfMeasureConversionFactor = detail.volumeUnitOfMeasureConversionFactor;
          witnessItem.purchaseUnitOfMeasureConversionFactor = detail.purchaseUnitOfMeasureConversionFactor;
          witnessItem.doorName = ps.getDoorName();
          witnessItem.inventoryItemId = pr.inventoryItemId;
          witnessItem.inventoryUnitOfMeasure = detail.inventoryUnitOfMeasureDisplay;
          witnessItem.isPracticeFavorite = detail.isPracticeFavorite;
          witnessItem.isRefrigerated = detail.refrigerated;
          witnessItem.isWitnessRequired = true;
          witnessItem.itemId = detail.itemId;
          witnessItem.parentComponent = parentComponent;
          witnessItem.ndc = detail.unitNdc;
          witnessItem.isBinItem = ps.isBin;
          witnessItem.isPreferredNdc = (ps.preferredItemId === detail.itemId);
          witnessItem.notes = '';
          witnessItem.positionContainerId = ps.getContainerId();
          witnessItem.formularyTypeId = detail.formularyTypeId;
          witnessItem.positionName = ps.getPositionName();
          witnessItem.reqDispenseUnitAdministerQuantity = 0;
          witnessItem.supplierProductInclusionId = detail.supplierProductInclusionId;
          witnessItem.isMultiLotYN = ps.lotTrackingRequired;
          witnessItem.isDiscontinued = pr.isDiscontinuedStatus();
          witnessItem.witnessUserPartyId = witnessUserId;
          witnessItem.witnessUserFullName = witnessUserName;
          witnessItem.storagePosition = ps.storagePosition;
          witnessItem.isAssigned = !ps.isUnassignedStatus();
          witnessItem.productId = detail.productId;
          witnessItem.qtyOnHandInCabinet = lot.lotQuantity;
          witnessItem.qtyOnHandInUTH = 0;
          witnessItem.userInputQtyInvUnitInCabinet = 0;
          witnessItem.userInputQtyInvUnitInUTH = 0;
          witnessItem.lotExpirationDate = lot.expirationDate;
          witnessItem.lotNumber = lot.lotNumber;
          witnessItem.isMultiLotYN = ps.lotTrackingRequired;
          witnessItem.sapMaterialNumber = detail.sapMaterialNumber;
          witnessItem.is340B = pr.is340BStatus();
          witnessItem.isBuyIn = pr.isBuyInStatus();
          witnessItem.isPatientOwned = ps.isPatientOwnedStatus();
          witnessItem.isItem505b2 = pr.isItem505b2Status();
          witnessItems.push(witnessItem);
        });
      }),
    );
    return witnessItems;
  }

  sortWitnessLotList(lotItems: WitnessItem[]): WitnessItem[] {
    return lotItems.sort((a, b) => this.sortExpirationDates(a, b));
  }

  sortExpirationDates(a: WitnessItem, b: WitnessItem): number {
    const date1ForCompare = Date.parse(DateTimeHelper.formatDateTime(a.lotExpirationDate, DateFormat.USADateOnly));
    const date2ForCompare = Date.parse(DateTimeHelper.formatDateTime(b.lotExpirationDate, DateFormat.USADateOnly));

    if (isNaN(date1ForCompare) && isNaN(date2ForCompare)) {
      return 1; // arbitrarily
    } else if (isNaN(date1ForCompare)) {
      return 1; // moves date1 further down the list
    } else if (isNaN(date2ForCompare)) {
      return -1; // move date 1 up the list and date 2 down the list
    } else if (date1ForCompare < date2ForCompare) {
      return -1;
    } else if (date1ForCompare > date2ForCompare) {
      return 1;
    } else {
      if (a.qtyOnHandInCabinet > b.qtyOnHandInCabinet) {
        return -1;
      } else if (a.qtyOnHandInCabinet < b.qtyOnHandInCabinet) {
        return 1;
      } else {
        return 0;
      }
    }
  }

  mapPurchaseOrderDetailViewModelToWitnessItemSummary(orderLine: PurchaseOrderDetailViewModel,
    parentComponent: ParentComponentTypes): WitnessItem[] {
    const witnessItems: WitnessItem[] = [];

    const witnessItem = this.mapPurchaseOrderDetailViewModelToWitnessItem(orderLine, parentComponent);
    witnessItem.qtyOnHandInCabinet = orderLine.quantityOnHandInventoryUnits;
    witnessItem.qtyOnHandInUTH = 0;
    witnessItem.userInputQtyInvUnitInUTH = 0;
    witnessItem.userInputQtyInvUnitInCabinet = 0;
    witnessItem.lotExpirationDate = null;
    witnessItem.lotNumber = '';
    witnessItem.genericName = orderLine.genericName;
    witnessItems.push(witnessItem);
    return witnessItems;
  }

  witnessInventory(witnessItems: WitnessItem[]): Observable<Transaction> {
    const stationInfo: StorageStation = this.stationInfoService.station.getValue();
    const clinicDetails = this.practiceClinicInfo.getValue();
    const transactionType = this.commonDataService.getTransactionTypeById(InventoryTransactionTypes.WitnessAdjust);

    // TODO: replace when expanding pasts OSC to other suppliers
    const targetSupplierPartyId = 1;
    const transactionRequest: TransactionRequest = {
      transactionEvent: {
        clinicName: clinicDetails.clinicName,
        clinicPartyId: clinicDetails.clinicId,
        fromStationContainerId: stationInfo.id,
        fromStationName: stationInfo.name,
        itemDetails: [] as EventDetailInfo[],
        practicePartyName: clinicDetails.practiceName,
        practicePartyId: clinicDetails.practiceId,
        serviceDate: new Date(),
        transactionTypeId: transactionType.transactionTypeId,
        transactionTypeName: transactionType.name,
        facilityId: stationInfo.facilityId,
        targetSupplierPartyId,
        stationAccountNumber: stationInfo.acctNumber,
      },
    };

    const eventDetails = this.createEventDetails(witnessItems);
    eventDetails.forEach(detail => transactionRequest.transactionEvent.itemDetails.push(detail));
    if (transactionRequest.transactionEvent.itemDetails.length > 0) {
      const saveToApi = (witnessItems[0].isMultiLotYN || witnessItems[0].isLotTracking) ?
        this.witnessAdjustApi.witnessStationMultiLot(transactionRequest) :
        this.witnessAdjustApi.witnessStationSingleLot(transactionRequest);
      return saveToApi.pipe(
        concatMap(transaction => this.stationInfoService.refreshInventoryCacheAsync().pipe(map(() => transaction))),
      );
    } else {
      return throwError(new Error('No change detected'));
    }
  }

  validateUserContext(userContextValidation: UserContextValidation): Observable<ValidateContextResponse> {
    return this.usersApi.validateUserContextV2(userContextValidation)
      .pipe(
        map(response => response as ValidateContextResponse),
        catchError(response => this.handleErrorResponse(response)),
      );
  }

  sortWitnessItemListByDisplayName(selectedWitnessLoginItems: WitnessItem[]): WitnessItem[] {
    return selectedWitnessLoginItems.sort((a: WitnessItem, b: WitnessItem): number => {
      if (a.displayName < b.displayName) { return -1; }
      if (a.displayName > b.displayName) { return 1; }
      return 0;
    });
  }

  userClaims(credential: RopcUserContract): Observable<UserClaims> {
    return this.identityApi.userClaims(credential)
      .pipe(
        map(response => response as UserClaims),
        catchError(response => {
          if (response.error === this.errorMessage) {
            this.inActiveUser = true;
          } else {
            this.inActiveUser = false;
          }
          return this.handleErrorResponse(response);
        }),
      );
  }

  sortWitnessItemPositionAscending(witnessItem: WitnessItem[]): WitnessItem[] {
    if (!AbsgArrayHelper.isNil(witnessItem) && witnessItem.length > 0) {
      return witnessItem.sort((d1, d2) => {
        if (d1.positionName && d2.positionName) {
          const n1 = Number(d1.positionName.split(' ')[1]);
          const n2 = Number(d2.positionName.split(' ')[1]);
          if (n1 > n2) {
            return 1;
          }
          if (n1 < n2) {
            return -1;
          }
          //non skip item should remain on top for the same position
          if (!AbsgArrayHelper.isNil(d1.skipTransaction) && !AbsgArrayHelper.isNil(d2.skipTransaction)) {
            const t1 = d1.skipTransaction ? 1 : 0;
            const t2 = d2.skipTransaction ? 1 : 0;
            return t1 > t2 ? 1 : -1;
          }
          return 0;

        } else {
          return 0;
        }
      });
    }
    return [];
  }

  sortBinWitnessItems(witnessItems: WitnessItem[]): WitnessItem[] {
    return AbsgArrayHelper.sortBy(witnessItems, (position) => [!position.isPreferredNdc, !position.isPracticeFavorite, position.displayName]);
  }

  getWitnessItemsForPositionDiscrepancies(witnessItems: WitnessItem[]): WitnessItem[] {
    // Which Positions have discrepancies?
    const positionDiscrepancyIds = AbsgArrayHelper.uniq(
      AbsgArrayHelper.map(
        AbsgArrayHelper.filter(witnessItems, item => this.doesItemHaveDiscrepancy(item)),
        w => w.positionContainerId));
    // We want all items related to that position
    return AbsgArrayHelper.filter(witnessItems, w => positionDiscrepancyIds.indexOf(w.positionContainerId) > -1);
  }

  doesItemHaveDiscrepancy(item: WitnessItem): boolean {
    const totalQohBeforeInvUnit = CalculationHelper.convertToQuantity(item.qtyOnHandInCabinet,
      item.dispenseUnitOfMeasureConversionFactor, item.inventoryUnitOfMeasureConversionFactor);

    return totalQohBeforeInvUnit !== item.userInputQtyInvUnitInCabinet;
  }

  /***
   * Dont want to provide manageposition service in shared module added this in witness service.
   * Returns the preferred Item productDetail if there is one
   * Or it will return the productDetail for the  cheapest item not found elsewhere on the station if no preferred
   * If neither it will just return the first item in the list
   */
  getAutoReorderItemIdForTransactionRequest(containerId: number,
    productId: number, productDetails: ProductDetail[]): Observable<ProductDetail> {
    // Has a preferred Item
    const preferredItemId = this.settingsService.getPreferredItemIdSetting(containerId);
    if (preferredItemId) {
      const productDetail: ProductDetail = AbsgArrayHelper.find(productDetails, (product) => product.itemId === preferredItemId);
      if (!AbsgArrayHelper.isNil(productDetail)) {
        return of(productDetail);
      } else {
        return this.getAutoReorderItemDetail(containerId, productId, productDetails);
      }
    } else {
      return this.getAutoReorderItemDetail(containerId, productId, productDetails);
    }
  }

  private getAutoReorderItemDetail(containerId: number,
    productId: number, productDetails: ProductDetail[]): Observable<ProductDetail> {
    return this.stationInfoService.getCachedInventoryItems().pipe(take(1),
      concatMap((allItems) => {
        // Do not include any items assigned elsewhere in the station
        const excludeList = this.getSupplierProductInclusionIds(allItems.filter(i => i.positionContainerId !== containerId));
        const request = this.stationInfoService.generateGetCheapestEquivilantRequest(excludeList);
        return this.medicalEquivalentApi.getCheapestEquivilantForProduct(productId, request)
          .pipe(map(productDetail => {
            if (productDetail) {
              return productDetail;
            } else {
              return productDetails[0];
            }
          }));
      }));
  }

  mapPositionsToWitnessItem(positions: Position[], parentComponent: ParentComponentTypes,
    witnessUserId?: number, witnessUserName?: string): WitnessItem[] {
    let witnessPositionItems: WitnessItem[] = [];
    witnessPositionItems = this.mapPositionsToWitnessItems(positions, parentComponent, witnessUserId, witnessUserName);

    witnessPositionItems = this.sortWitnessItemPositionAscending(this.sortBinWitnessItems(witnessPositionItems));
    return witnessPositionItems;
  }

  private AssignManagePositionsToWitness(managePosition: ManagePosition,
    parentComponent: ParentComponentTypes, witnessItems: WitnessItem[],
  ): WitnessItem[] {
    let inventoryItem = null;
    if (managePosition.isBinned()) {
      inventoryItem = managePosition.inventoryItems
        .find(elem => elem.supplierProductInclusionId === managePosition.productDetail.supplierProductInclusionId);
    } else {
      inventoryItem = managePosition.inventoryItem;
    }

    if (!AbsgArrayHelper.isNil(inventoryItem) && !AbsgArrayHelper.isNil(inventoryItem.inventoryItemProducts) &&
      inventoryItem.inventoryItemProducts.length > 0
    ) {
      inventoryItem.inventoryItemProducts.forEach(identifier => {
        if (!AbsgArrayHelper.isNil(identifier) && (identifier.lotQuantity > 0 || identifier.lotQuantityUnderhood > 0 ||
          parentComponent === ParentComponentTypes.Restock || parentComponent === ParentComponentTypes.Unload || parentComponent === ParentComponentTypes.Move)
        ) {
          const witnessItem = this.mapManagePositionToWitnessItem(managePosition, parentComponent);
          witnessItem.qtyOnHandInCabinet = identifier.lotQuantity;
          witnessItem.qtyOnHandInUTH = identifier.lotQuantityUnderhood;
          witnessItem.lotExpirationDate = identifier.expirationDate;
          witnessItem.lotNumber = identifier.lotNumber;
          witnessItem.userInputQtyInvUnitInCabinet = 0;
          witnessItem.isMultiLotYN = managePosition.isLotTrackingOn;
          witnessItem.isDiscontinued = managePosition.productDetail.isDiscontinued;
          witnessItem.witnessUserPartyId = managePosition.witnessUserId;
          witnessItem.witnessUserFullName = managePosition.witnessUserName;
          witnessItem.isAssigned = managePosition.storagePosition ? managePosition.storagePosition.id > 0 : false;
          witnessItems.push(witnessItem);
        }
      });
    } else {
      if (parentComponent === ParentComponentTypes.Restock || parentComponent === ParentComponentTypes.Unload
        || parentComponent === ParentComponentTypes.Move) {
        const witnessItem = this.mapManagePositionToWitnessItem(managePosition, parentComponent);
        witnessItem.qtyOnHandInCabinet = 0;
        witnessItem.qtyOnHandInUTH = 0;
        witnessItem.lotExpirationDate = null;
        witnessItem.lotNumber = null;
        witnessItem.userInputQtyInvUnitInCabinet = 0;
        witnessItem.isMultiLotYN = managePosition.isLotTrackingOn;
        witnessItem.isDiscontinued = managePosition.productDetail.isDiscontinued;
        witnessItem.witnessUserPartyId = managePosition.witnessUserId;
        witnessItem.witnessUserFullName = managePosition.witnessUserName;
        witnessItem.isAssigned = managePosition.storagePosition ? managePosition.storagePosition.id > 0 : false;
        witnessItems.push(witnessItem);
      }
    }
    return witnessItems;
  }

  private mapPurchaseOrderDetailViewModelToWitnessItem(orderLine: PurchaseOrderDetailViewModel,
    parentComponent: ParentComponentTypes): WitnessItem {
    const witnessItem = new WitnessItem();
    witnessItem.accessPointId = orderLine.getAssociatedAccessPointId();
    witnessItem.displayName = orderLine.displayName;
    witnessItem.alternateName = orderLine.alternateName;
    witnessItem.doorName = orderLine.getAssociatedAccessPointName();
    witnessItem.inventoryUnitOfMeasure = orderLine.inventoryUnit;
    witnessItem.inventoryUnitOfMeasureConversionFactor = orderLine.inventoryUnitFactor;
    witnessItem.dispenseUnitOfMeasureConversionFactor = orderLine.dispenseUnitFactor;
    witnessItem.purchaseUnitOfMeasureConversionFactor = orderLine.purchaseUnitFactor;
    witnessItem.isPracticeFavorite = orderLine.favoriteFlag;
    witnessItem.isRefrigerated = orderLine.refrigeratedFlag;
    witnessItem.genericName = orderLine.genericName;
    witnessItem.isWitnessRequired = true;
    witnessItem.itemId = orderLine.itemId;
    witnessItem.storagePosition = orderLine.storagePosition;
    witnessItem.parentComponent = parentComponent;
    witnessItem.ndc = orderLine.ndc;
    witnessItem.isBinItem = orderLine.isBinned();
    witnessItem.notes = '';
    witnessItem.positionContainerId = orderLine.storagePosition.id;
    witnessItem.isDiscontinued = orderLine.discontinuedFlag;
    witnessItem.formularyTypeId = FormularyType.MasterOwnedItem; // for now, only master formulary items can be added to PO
    witnessItem.doorName = orderLine.getAssociatedAccessPointName();
    witnessItem.positionName = this.getPositionNameFromDoorPosition(orderLine.storagePosition.name);
    witnessItem.reqDispenseUnitAdministerQuantity = 0;
    witnessItem.supplierProductInclusionId = orderLine.supplierProductInclusionId;
    witnessItem.isDiscontinued = orderLine.isDiscontinuedStatus();
    witnessItem.userInputQtyInvUnitInCabinet = 0;
    witnessItem.isAssigned = true;
    witnessItem.isItem505b2 = orderLine.isItem505b2;
    return witnessItem;
  }

  private mapManagePositionToWitnessItem(managePosition: ManagePosition, parentComponent: ParentComponentTypes): WitnessItem {
    const witnessItem = new WitnessItem();
    if (managePosition.storagePosition) {
      if (managePosition.storagePosition.id > 0) {
        witnessItem.accessPointId = managePosition.getAssociatedAccessPointId();
        witnessItem.positionContainerId = managePosition.inventoryItem.positionContainerId;
        witnessItem.isAssigned = true;
      } else {
        witnessItem.isAssigned = false;
        witnessItem.positionContainerId = 0;
      }
      if (managePosition.storagePosition.name && managePosition.storagePosition.name.length > 0) {
        witnessItem.doorName = managePosition.getAssociatedAccessPointName();
        witnessItem.positionName = managePosition.getPositionName().replace('Position', 'Pos');
      }
    }
    witnessItem.productId = managePosition.productDetail.productId;
    witnessItem.displayName = managePosition.productDetail.displayName;
    witnessItem.alternateName = managePosition.productDetail.alternateName;
    witnessItem.genericName = managePosition.productDetail.genericName;
    witnessItem.gcnSequenceNumber = managePosition.productDetail.gcnSequenceNumber;
    witnessItem.dispenseUnitOfMeasureConversionFactor = managePosition.productDetail.dFactor;
    witnessItem.inventoryUnitOfMeasureConversionFactor = managePosition.productDetail.iFactor;
    witnessItem.volumeUnitOfMeasureConversionFactor = managePosition.productDetail.vFactor;
    witnessItem.purchaseUnitOfMeasureConversionFactor = managePosition.productDetail.pFactor;
    witnessItem.inventoryItemId = managePosition.inventoryItem.inventoryItemId;
    witnessItem.inventoryUnitOfMeasure = managePosition.productDetail.iUomDisplay;
    witnessItem.isPracticeFavorite = managePosition.productDetail.isPracticeFavorite;
    witnessItem.isRefrigerated = managePosition.productDetail.refrigerated;
    witnessItem.isWitnessRequired = true;
    witnessItem.itemId = managePosition.productDetail.itemId;
    witnessItem.storagePosition = managePosition.storagePosition;
    witnessItem.parentComponent = parentComponent;
    witnessItem.ndc = managePosition.productDetail.unitNdc;
    witnessItem.isBinItem = managePosition.isBinned();
    witnessItem.isPreferredNdc = this.getPreferredNdc(witnessItem.positionContainerId, witnessItem.itemId);
    witnessItem.notes = '';
    witnessItem.formularyTypeId = managePosition.productDetail.formularyTypeId;
    witnessItem.reqDispenseUnitAdministerQuantity = 0;
    witnessItem.supplierProductInclusionId = managePosition.productDetail.supplierProductInclusionId;
    witnessItem.isDiscontinued = managePosition.productDetail.isDiscontinued;
    witnessItem.witnessUserPartyId = managePosition.witnessUserId;
    witnessItem.witnessUserFullName = managePosition.witnessUserName;
    witnessItem.userInputQtyInvUnitInCabinet = 0;
    witnessItem.sapMaterialNumber = managePosition.productDetail.itemId;
    witnessItem.isPatientOwned = managePosition.isPatientOwnedStatus();
    witnessItem.patientInfo = managePosition.patientInfo;
    witnessItem.patientOwnedItemTypeShortName = managePosition.patientOwnedItemTypeShortName;
    witnessItem.isItem505b2 = managePosition.productDetail.isItem505b2;
    witnessItem.is340B = managePosition.productDetail.is340B;
    witnessItem.isBuyIn = managePosition.productDetail.isBuyIn;
    return witnessItem;
  }

  private getPreferredNdc(containerId: number, itemId: string): boolean {
    const preferredItem = this.settingsService.getPreferredItemIdSetting(containerId);
    if (preferredItem === itemId) {
      return true;
    }
    return false;
  }

  private mapInventoryItemToWitnessItem(invItem: InventoryItem, parentComponent: ParentComponentTypes): WitnessItem {
    const witnessItem = new WitnessItem();
    witnessItem.accessPointId = this.getAccessPointId(invItem);
    witnessItem.displayName = invItem.name;
    witnessItem.alternateName = invItem.tradeName;
    witnessItem.genericName = invItem.genericName;
    witnessItem.gcnSequenceNumber = invItem.gcnSequenceNumber;
    witnessItem.dispenseUnitOfMeasureConversionFactor = invItem.dispenseUOMConversionFactor;
    witnessItem.inventoryUnitOfMeasureConversionFactor = invItem.inventoryUOMConversionFactor;
    witnessItem.volumeUnitOfMeasureConversionFactor = invItem.volumeUOMConversionFactor;
    witnessItem.purchaseUnitOfMeasureConversionFactor = invItem.purchaseUOMConversionFactor;
    witnessItem.doorName = this.getDoorName(invItem);
    witnessItem.inventoryItemId = invItem.id;
    witnessItem.inventoryUnitOfMeasure = invItem.inventoryUnit;
    witnessItem.isPracticeFavorite = invItem.isPracticeFavorite;
    witnessItem.isRefrigerated = invItem.refrigerated;
    witnessItem.isWitnessRequired = true;
    witnessItem.itemId = invItem.itemId;
    witnessItem.parentComponent = parentComponent;
    witnessItem.ndc = invItem.ndc;
    witnessItem.isBinItem = invItem.isBin;
    witnessItem.notes = '';
    witnessItem.positionContainerId = invItem.storagePosition.id;
    witnessItem.formularyTypeId = invItem.formularyTypeId;
    witnessItem.positionName = this.getPositionName(invItem);
    witnessItem.reqDispenseUnitAdministerQuantity = invItem.quantitySelected;
    witnessItem.supplierProductInclusionId = invItem.supplierProductInclusionId;
    witnessItem.isMultiLotYN = this.isMultiLot(invItem);
    witnessItem.isLotTracking = invItem.multiLotYN; // only use for calling single lot or multilot api, DO NOT USE FOR ANYTHING ELSE
    witnessItem.isDiscontinued = invItem.isDiscontinued;
    witnessItem.witnessUserPartyId = invItem.witnessUserId;
    witnessItem.witnessUserFullName = invItem.witnessUserName;
    witnessItem.storagePosition = invItem.storagePosition;
    witnessItem.isAssigned = this.getAccessPointId(invItem) > 0;
    witnessItem.productId = invItem.productId;
    witnessItem.sapMaterialNumber = invItem.sapMaterialNumber;
    witnessItem.isPatientOwned = invItem.isPatientOwnedStatus();
    witnessItem.patientInfo = invItem.patientInfo;
    witnessItem.patientOwnedItemTypeShortName = invItem.patientOwnedItemTypeShortName;
    witnessItem.is340B = invItem.is340B;
    witnessItem.isBuyIn = invItem.isBuyIn;
    witnessItem.isItem505b2 = invItem.isItem505b2;
    return witnessItem;
  }

  private isMultiLot(invItem: InventoryItem): boolean {
    const cabinetLotWithQoh = invItem.inventoryItemProducts.filter(x => x.lotQuantity > 0 || x.lotQuantityUnderhood > 0).length;
    if (cabinetLotWithQoh > 1) {
      return true;
    } else {
      return false;
    }
  }

  private createEventDetails(witnessItems: WitnessItem[]): EventDetailInfo[] {
    const eventDetails = new Array<EventDetailInfo>();
    witnessItems.forEach(witnessItem => {

      const detailPosition = this.createEventDetailInfo(witnessItem);
      detailPosition.qtyOnHandAfter = CalculationHelper.convertToQuantity(witnessItem.userInputQtyInvUnitInCabinet,
        witnessItem.inventoryUnitOfMeasureConversionFactor, witnessItem.dispenseUnitOfMeasureConversionFactor);

      detailPosition.qtyOnHandBefore = witnessItem.qtyOnHandInCabinet;

      // Why are we not converting into dispense units here?
      detailPosition.positionContainerId = witnessItem.positionContainerId;
      detailPosition.reqQtyInDispenseUnits = witnessItem.userInputQtyInvUnitInCabinet;
      detailPosition.reqQtyInDispenseUnitsFromCabinet = witnessItem.qtyOnHandInCabinet;
      detailPosition.reqQtyInDispenseUnitsFromUnderHood = 0; // Witness Items not allowed underhood
      detailPosition.reqQtyInDispenseUnitsToCabinet =
        CalculationHelper.convertToQuantity(witnessItem.userInputQtyInvUnitInCabinet,
          witnessItem.inventoryUnitOfMeasureConversionFactor, witnessItem.dispenseUnitOfMeasureConversionFactor);
      detailPosition.reqQtyInDispenseUnitsToUnderHood = 0; // Witness Items not allowed underhood
      detailPosition.detailNotes = witnessItem.notes;
      detailPosition.witnessUserId = witnessItem.witnessUserPartyId;
      detailPosition.witnessUserName = witnessItem.witnessUserFullName;

      if (detailPosition.reqQtyInDispenseUnitsFromCabinet !== detailPosition.reqQtyInDispenseUnitsToCabinet ||
        detailPosition.reqQtyInDispenseUnitsFromUnderHood !== detailPosition.reqQtyInDispenseUnitsToUnderHood) {
        eventDetails.push(detailPosition);
      }
    });
    return eventDetails;
  }

  private createEventDetailInfo(witnessItem: WitnessItem): EventDetailInfo {
    const eventDetail: EventDetailInfo = {
      dispenseConversionFactor: witnessItem.dispenseUnitOfMeasureConversionFactor,
      inventoryConversionFactor: witnessItem.inventoryUnitOfMeasureConversionFactor,
      purchaseConversionFactor: witnessItem.purchaseUnitOfMeasureConversionFactor,
      inventoryItemId: witnessItem.inventoryItemId,
      lotExpirationDate: witnessItem.lotExpirationDate,
      lotNumber: witnessItem.lotNumber,
      productDescription: witnessItem.displayName,
      reqWasteQtyInDispenseUnits: 0,
      supplierProductInclusionId: witnessItem.supplierProductInclusionId,
      supplierProductItemId: witnessItem.itemId,
      sapMaterialNumber: witnessItem.sapMaterialNumber,
    };

    if (!AbsgArrayHelper.isNil(witnessItem.lotExpirationDate)) {
      eventDetail.lotExpirationDate = witnessItem.lotExpirationDate;
    } else {
      eventDetail.lotExpirationDate = null;
    }

    if (!AbsgArrayHelper.isNil(witnessItem.lotNumber)) {
      eventDetail.lotNumber = witnessItem.lotNumber;
    } else {
      eventDetail.lotNumber = null;
    }

    eventDetail.returnNotes = witnessItem.notes;
    eventDetail.positionContainerId = witnessItem.positionContainerId;

    // auto re-order properties
    if (witnessItem.isBinItem) {
      eventDetail.isBin = witnessItem.isBinItem;
      eventDetail.isPreferredNDC = witnessItem.isPreferredNdc;
      eventDetail.spiIdForItemOrdered = witnessItem.autoReorderspInclusionId;
      eventDetail.supplierProductItemId = witnessItem.autoReorderspItemId;
      eventDetail.productDescription = witnessItem.autoReorderTradeName;
    }

    return this.populateAutoReOrder(eventDetail, witnessItem);
  }

  private populateAutoReOrder(detail: EventDetailInfo, witnessItem: WitnessItem): EventDetailInfo {
    const containerId = detail.positionContainerId;
    const supplierProductInclusionId = witnessItem.getSupplierProductInclusionId();

    detail.autoReorderEnabled =
      this.settingsService.getAutoReOrderSetting(containerId, supplierProductInclusionId, witnessItem.formularyTypeId);
    detail.itemMinBefore = this.settingsService.getMinSetting(containerId);
    detail.itemMinAfter = detail.itemMinBefore;
    detail.itemMaxBefore = this.settingsService.getMaxSetting(containerId);
    detail.itemMaxAfter = detail.itemMaxBefore;

    return detail;
  }

  private getDoorName(invItem: InventoryItem): string {
    if (!AbsgArrayHelper.isNil(invItem) && name !== undefined && !AbsgArrayHelper.isNil(invItem.storagePosition)
      && !AbsgArrayHelper.isNil(invItem.storagePosition.name)
      && invItem.storagePosition.name.length > 0) {
      return invItem.storagePosition.name.split('-')[0].trim();
    }
    return '';
  }

  private getPositionName(invItem: InventoryItem): string {
    if (!AbsgArrayHelper.isNil(invItem) && !AbsgArrayHelper.isNil(invItem.storagePosition) && !AbsgArrayHelper.isNil(invItem.storagePosition.name)
      && invItem.storagePosition.name !== undefined && invItem.storagePosition.name.length > 0) {
      return invItem.storagePosition.name.split('-')[1].replace('Position', 'Pos').trim();
    }
    return '';
  }

  private getPositionNameFromDoorPosition(name: string): string {
    if (!AbsgArrayHelper.isNil(name && name !== undefined && name.length > 0)) {
      return name.split('-')[1].replace('Position', 'Pos').trim();
    }
    return '';
  }

  private getAccessPointId(invItem: InventoryItem): number {
    const doorName = this.getDoorName(invItem);
    if (!AbsgArrayHelper.isNil(doorName) && doorName !== '') {
      const accessPoint = this.stationInfoService.getAccessPointByName(doorName);
      if (!AbsgArrayHelper.isNil(accessPoint)) {
        return accessPoint.id;
      }
    }
    return 0;
  }

  getSupplierProductInclusionIds(inventoryItems: InventoryItemSummary[]): number[] {
    if (!inventoryItems) {
      return [];
    }
    const ids = inventoryItems
      .filter(inventoryItemsSummary => inventoryItemsSummary?.supplierProductInclusionId)
      .map(inventoryItemsSummary => inventoryItemsSummary.supplierProductInclusionId);

    return ids;
  }
}
